import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import { message, Spin, Button, Tooltip, Alert } from 'antd';
import {
  PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined,
  BoldOutlined, ItalicOutlined, UnderlineOutlined, SaveOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
  FontSizeOutlined, CloseOutlined, ZoomInOutlined, ZoomOutOutlined, ExpandOutlined,
  UploadOutlined, LoadingOutlined, ScissorOutlined, CheckOutlined,
} from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

// ===== 可编辑文字组件 v4（稿定式三栏画布）=====
// 左栏：添加文字（标题/副标题/正文）+ 字体列表 + 图层清单
// 中间：深灰工作区画布（缩放/尺寸信息条）
// 右栏：选中文字的属性面板（字体/字号/B-I-U/对齐/行距字距/颜色/背景框/描边/透明度/图层/删除）
// 底图 = 原图直接加载（cleanBase，改字功能已下线，不再去字/识别/预填）；文字 = Fabric Textbox 图层。
// 导出：按原图分辨率 toDataURL → 上传现有 /api/upload/image。
// 依赖：fabric@5.3.0（import { fabric } from 'fabric'）。

// 系统默认字体栈（零加载成本，本机已装即生效；未装则浏览器自动 fallback）
const FONT_OPTIONS = [
  { label: '默认黑体', value: 'sans-serif' },
  { label: '微软雅黑', value: 'Microsoft YaHei' },
  { label: '苹方', value: 'PingFang SC' },
  { label: '黑体', value: 'SimHei' },
  { label: '宋体', value: 'SimSun' },
  { label: '楷体', value: 'KaiTi' },
  { label: '仿宋', value: 'FangSong' },
];

// 添加文字预设（字号按画布宽度比例）
const TEXT_PRESETS = [
  { key: 'title', label: '标题', text: '标题', ratio: 0.085, weight: 'bold' },
  { key: 'subtitle', label: '副标题', text: '副标题', ratio: 0.05, weight: 'normal' },
  { key: 'body', label: '正文', text: '正文', ratio: 0.032, weight: 'normal' },
];

const MAX_PREFILL = 24;
const DEFAULT_TEXT_COLOR = '#2A2A2A';

// 裁切比例预设（value = 宽/高 比，null = 自由裁切）
const CROP_RATIOS = [
  { label: '自由', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
];

// 字体颜色快捷色板（一键应用，电商主图常用色）
const COLOR_PRESETS = ['#1A1A1A', '#FFFFFF', '#8C8C8C', '#D85A30', '#C0392B', '#B8860B', '#1F5C99', '#3F8600'];

// 热门文字样式预设（描边/背景框一键套用，电商主图常用样式）
const TEXT_STYLE_PRESETS = [
  { label: '白字·黑描边', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 2, textBackgroundColor: '' },
  { label: '白字·珊瑚描边', fill: '#FFFFFF', stroke: '#D85A30', strokeWidth: 2, textBackgroundColor: '' },
  { label: '白字·深色底', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 0, textBackgroundColor: 'rgba(0,0,0,0.55)' },
  { label: '珊瑚字·白描边', fill: '#D85A30', stroke: '#FFFFFF', strokeWidth: 2, textBackgroundColor: '' },
  { label: '金字·棕描边', fill: '#C9A063', stroke: '#5C3A1E', strokeWidth: 2, textBackgroundColor: '' },
  { label: '纯色·无修饰', fill: '#1A1A1A', stroke: '#000000', strokeWidth: 0, textBackgroundColor: '' },
];

function regionToRatio(region) {
  const s = String(region || '');
  let x = 0.5, y = 0.5;
  if (s.indexOf('左') >= 0) x = 0.26; else if (s.indexOf('右') >= 0) x = 0.74;
  if (s.indexOf('顶') >= 0 || s.indexOf('上') >= 0) y = 0.16;
  else if (s.indexOf('底') >= 0 || s.indexOf('下') >= 0) y = 0.84;
  return { x, y };
}

function bboxToRatio(bbox, w, h) {
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  const [x0, y0, x1, y1] = bbox.map(Number);
  if (![x0, y0, x1, y1].every((n) => Number.isFinite(n))) return null;
  const mx = Math.max(Math.abs(x0), Math.abs(x1));
  const my = Math.max(Math.abs(y0), Math.abs(y1));
  if (mx <= 1 && my <= 1) return { x0, y0, x1, y1 };
  if (mx <= 1000 && my <= 1000) return { x0: x0 / 1000, y0: y0 / 1000, x1: x1 / 1000, y1: y1 / 1000 };
  return { x0: x0 / w, y0: y0 / h, x1: x1 / w, y1: y1 / h };
}

async function ensureFonts(fonts) {
  if (!document.fonts) return;
  try {
    await Promise.all(fonts.map((f) => document.fonts.load('64px "' + f + '"')));
    await document.fonts.ready;
  } catch (e) { /* 系统字体未加载时忽略，浏览器自行 fallback */ }
}

function toHexColor(fill) {
  if (typeof fill === 'string' && fill.charAt(0) === '#') return fill.length === 7 ? fill : DEFAULT_TEXT_COLOR;
  return DEFAULT_TEXT_COLOR;
}

function loadBaseImage(url) {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        fabric.Image.fromURL(objUrl, (img) => (img ? resolve(img) : reject(new Error('底图解析失败'))), {});
      })
      .catch(() => {
        fabric.Image.fromURL(url, (img) => (img ? resolve(img) : reject(new Error('底图加载失败'))), { crossOrigin: 'anonymous' });
      });
  });
}

// ===== 通用小组件（内联）=====
function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#8A7A72', marginBottom: 6, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}
function IconBtn({ title, active, onClick, children }) {
  return (
    <Tooltip title={title}>
      <button
        type="button"
        onClick={onClick}
        style={{
          width: 32, height: 30, borderRadius: 6, cursor: 'pointer', fontSize: 14,
          border: '1px solid ' + (active ? '#D85A30' : '#E5DDD6'), color: active ? '#D85A30' : '#6B5D55',
          background: active ? '#FBE9E2' : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >{children}</button>
    </Tooltip>
  );
}
function NumInput({ value, onChange, min, max, step, width = 62, suffix }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width, height: 30, borderRadius: 6, border: '1px solid #E5DDD6', padding: '0 6px', fontSize: 13, color: '#33251F' }}
      />
      {suffix && <span style={{ fontSize: 12, color: '#B9A79E' }}>{suffix}</span>}
    </span>
  );
}
function ColorRow({ label, value, onClear, clearLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 12, color: '#6B5D55' }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {onClear && (
          <button type="button" onClick={onClear} style={{ border: 'none', background: 'none', color: '#B9A79E', fontSize: 12, cursor: 'pointer' }}>
            {clearLabel || '无'}
          </button>
        )}
        <input type="color" value={value} onChange={(e) => e.target.value && onChange(e.target.value)}
          style={{ width: 34, height: 28, border: '1px solid #E5DDD6', borderRadius: 6, padding: 2, background: '#fff', cursor: 'pointer' }} />
      </span>
    </div>
  );
}

export default function CanvasEditor({ init, onSaved, onClose }) {
  const canvasElRef = useRef(null);
  const stageRef = useRef(null);      // 画布工作区容器（算适合窗口用）
  const fcRef = useRef(null);
  const scaleRef = useRef(1);
  const fitScaleRef = useRef(0.5);
  const sizeRef = useRef({ w: 1024, h: 1024 });
  const activeRef = useRef(null);
  const itemsRef = useRef([]);
  const defaultFontRef = useRef('sans-serif');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [zoomPct, setZoomPct] = useState(50);
  const [serverFonts, setServerFonts] = useState([]);       // 服务器字体列表（uploads/fonts/）
  const [fontUploading, setFontUploading] = useState(false); // 字体上传中
  const [fontLoadingFamily, setFontLoadingFamily] = useState(''); // 正在加载的字体名（按需加载提示）
  const fontFileRef = useRef(null);
  const [, force] = useState({});
  const rerender = useCallback(() => force({}), []);

  // ===== 裁切状态 =====
  const [cropMode, setCropMode] = useState(false);        // 是否处于裁切模式
  const [cropRatio, setCropRatio] = useState(null);        // 当前裁切比例（null=自由）
  const cropRatioRef = useRef(null);                       // 比例 ref（事件处理器内读取最新值）
  const cropRectRef = useRef(null);                        // 裁切选框
  const cropShadesRef = useRef(null);                      // 选框外四块半透明遮罩

  const { cleanBase, textRegions = [], subject, baseHasText = false } = init || {};

  const syncItems = useCallback(() => {
    setItems(itemsRef.current.map((i) => ({ id: i.id, label: i.label })));
  }, []);

  const syncLabels = useCallback(() => {
    itemsRef.current.forEach((i) => { i.label = String(i.textbox.text || '').trim().replace(/\n/g, '') || '（空）'; });
    syncItems();
  }, [syncItems]);

  function addTextboxFromBbox(fc, content, w, h, bbox, color) {
    let left = w / 2, top = h / 2, width = Math.round(w * 0.6), fontSize = Math.max(12, Math.round(w * 0.06));
    let displayText = String(content || '文字');
    const r = bboxToRatio(bbox, w, h);
    if (r) {
      const bw = w * (r.x1 - r.x0), bh = h * (r.y1 - r.y0);
      left = w * (r.x0 + r.x1) / 2;
      top = h * (r.y0 + r.y1) / 2;
      const vertical = bh > bw * 1.5;
      if (vertical) {
        displayText = String(content || '文字').split('').join('\n');
        fontSize = Math.min(Math.max(10, Math.round(bw * 0.92)), Math.round(h * 0.18));
        width = Math.max(24, Math.round(bw * 1.15));
      } else {
        fontSize = Math.min(Math.max(10, Math.round(bh * 0.8)), Math.round(h * 0.18));
        width = Math.max(40, Math.round(bw * 1.05));
      }
    }
    const tb = new fabric.Textbox(displayText, {
      left, top, originX: 'center', originY: 'center',
      width, fontSize, fontFamily: defaultFontRef.current,
      fill: color || DEFAULT_TEXT_COLOR,
      stroke: '#000000', strokeWidth: 0,
      textAlign: 'center', fontWeight: 'normal',
      lineHeight: 1.0, charSpacing: 0, shadow: null,
    });
    fc.add(tb);
    const id = 't' + Date.now() + Math.random().toString(36).slice(2, 6);
    itemsRef.current.push({ id, textbox: tb, label: String(content || '文字') });
    syncItems();
    return tb;
  }

  function addTextboxAt(fc, content, w, h, rx, ry, color) {
    const tb = new fabric.Textbox(String(content || '文字'), {
      left: w * rx, top: h * ry, originX: 'center', originY: 'center',
      width: Math.round(w * 0.6), fontSize: Math.max(12, Math.round(w * 0.06)),
      fontFamily: defaultFontRef.current,
      fill: color || DEFAULT_TEXT_COLOR, stroke: '#000000', strokeWidth: 0,
      textAlign: 'center', shadow: null,
    });
    fc.add(tb);
    const id = 't' + Date.now() + Math.random().toString(36).slice(2, 6);
    itemsRef.current.push({ id, textbox: tb, label: String(content || '文字') });
    syncItems();
    return tb;
  }

  const selectItem = (id) => {
    const fc = fcRef.current; if (!fc) return;
    const it = itemsRef.current.find((x) => x.id === id);
    if (!it) return;
    fc.setActiveObject(it.textbox);
    fc.requestRenderAll();
    setActiveId(id);
    rerender();
  };

  const zoomTo = (s) => {
    const fc = fcRef.current; if (!fc) return;
    const { w, h } = sizeRef.current;
    const clamped = Math.max(0.08, Math.min(4, s));
    scaleRef.current = clamped;
    fc.setDimensions({ width: Math.round(w * clamped), height: Math.round(h * clamped) });
    fc.setZoom(clamped);
    fc.requestRenderAll();
    setZoomPct(Math.round(clamped * 100));
    rerender();
  };

  const fitToScreen = () => {
    const stage = stageRef.current; if (!stage) return;
    const { w, h } = sizeRef.current;
    const s = Math.min((stage.clientWidth - 60) / w, (stage.clientHeight - 60) / h, 1);
    fitScaleRef.current = Math.max(0.08, s);
    zoomTo(fitScaleRef.current);
  };

  // ===== 初始化画布 =====
  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        await ensureFonts(FONT_OPTIONS.map((f) => f.value));
        if (disposed || !canvasElRef.current || !cleanBase) return;

        const fc = new fabric.Canvas(canvasElRef.current, {
          preserveObjectStacking: true,
          backgroundColor: '#ffffff',
        });
        fcRef.current = fc;

        const img = await loadBaseImage(cleanBase);
        if (disposed) return;

        const w = img.width || 1024;
        const h = img.height || 1024;
        sizeRef.current = { w, h };

        const stage = stageRef.current;
        const maxW = Math.max(320, (stage ? stage.clientWidth : 900) - 60);
        const maxH = Math.max(320, (stage ? stage.clientHeight : 600) - 60);
        const scale = Math.min(1, maxW / w, maxH / h);
        scaleRef.current = scale;
        fitScaleRef.current = scale;
        setZoomPct(Math.round(scale * 100));
        fc.setDimensions({ width: Math.round(w * scale), height: Math.round(h * scale) });
        fc.setZoom(scale);

        img.set({ selectable: false, evented: false });
        fc.setBackgroundImage(img, () => fc.requestRenderAll());

        if (subject) {
          try {
            const subImg = await loadBaseImage(subject);
            if (!disposed && subImg) {
              subImg.set({ left: 0, top: 0, originX: 'left', originY: 'top', selectable: true, evented: true, perPixelTargetFind: true });
              fc.add(subImg);
            }
          } catch (e) { console.log('产品元素层加载失败：' + (e.message || e)); }
        }

        if (!baseHasText) {
          (textRegions || []).slice(0, MAX_PREFILL).forEach((t) => {
            const content = t['内容'] != null ? t['内容'] : t.content;
            // 乱码防御：模型自认读不出 / 中英混排的识别结果不预填，避免画布出现乱码文字
            const s = String(content || '').trim();
            if (/乱码|无法识别|识别不|看不清|不清楚|模糊/.test(s)) return;
            if (/[A-Za-z]/.test(s) && /[\u3400-\u9FFF]/.test(s)) return;
            const color = t.color || t['颜色'] || null;
            if (t.bbox) {
              addTextboxFromBbox(fc, content || '文字', w, h, t.bbox, color);
            } else {
              const r = regionToRatio(t['位置']);
              addTextboxAt(fc, content || '文字', w, h, r.x, r.y, color);
            }
          });
        }

        const onSel = (e) => {
          const o = (e.selected && e.selected[0]) || null;
          activeRef.current = o;
          const it = itemsRef.current.find((x) => x.textbox === o);
          setActiveId(it ? it.id : null);
          rerender();
        };
        fc.on('selection:created', onSel);
        fc.on('selection:updated', onSel);
        fc.on('selection:cleared', () => { activeRef.current = null; setActiveId(null); rerender(); });
        fc.on('object:modified', () => { syncLabels(); rerender(); });
        fc.on('text:changed', () => { syncLabels(); rerender(); });

        // 裁切选框：拖动时限制在画布内 + 同步遮罩
        fc.on('object:moving', (e) => {
          const rect = e.target;
          if (!rect || rect !== cropRectRef.current) return;
          const { w, h } = sizeRef.current;
          const cw = rect.width * rect.scaleX, chh = rect.height * rect.scaleY;
          rect.set({
            left: Math.max(0, Math.min(w - cw, rect.left)),
            top: Math.max(0, Math.min(h - chh, rect.top)),
          });
          updateCropShades();
        });
        // 裁切选框：缩放时限制在画布内 + 锁比例 + 同步遮罩
        fc.on('object:scaling', (e) => {
          const rect = e.target;
          if (!rect || rect !== cropRectRef.current) return;
          const { w, h } = sizeRef.current;
          const maxSx = (w - rect.left) / rect.width;
          const maxSy = (h - rect.top) / rect.height;
          const ratio = cropRatioRef.current;
          let sx = Math.min(rect.scaleX, maxSx);
          let sy = Math.min(rect.scaleY, maxSy);
          if (ratio) {
            sy = Math.min(sx / ratio, maxSy);
            sx = Math.min(sy * ratio, maxSx);
            sy = Math.min(sx / ratio, maxSy);
          }
          if (rect.width * sx < 40) sx = 40 / rect.width;
          if (rect.height * sy < 40) sy = 40 / rect.height;
          rect.set({ scaleX: sx, scaleY: sy });
          updateCropShades();
        });

        fc.requestRenderAll();
      } catch (e) {
        message.error('画布加载失败：' + (e.message || e));
      } finally {
        if (!disposed) setLoading(false);
      }
    })();

    return () => {
      disposed = true;
      if (fcRef.current) { try { fcRef.current.dispose(); } catch (e) {} fcRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanBase, baseHasText]);

  const active = activeRef.current;
  const isText = !!active && (active.type === 'textbox' || active.type === 'i-text' || active.type === 'text');
  const isImage = !!active && active.type === 'image';

  const addPresetText = (p) => {
    const fc = fcRef.current; if (!fc) return;
    const { w, h } = sizeRef.current;
    const tb = new fabric.Textbox(p.text, {
      left: w / 2, top: h / 2, originX: 'center', originY: 'center',
      width: Math.round(w * 0.7), fontSize: Math.max(14, Math.round(w * p.ratio)),
      fontFamily: defaultFontRef.current, fill: DEFAULT_TEXT_COLOR,
      stroke: '#000000', strokeWidth: 0, textAlign: 'center',
      fontWeight: p.weight, lineHeight: 1.0, charSpacing: 0, shadow: null,
    });
    fc.add(tb);
    const id = 't' + Date.now() + Math.random().toString(36).slice(2, 6);
    itemsRef.current.push({ id, textbox: tb, label: p.text });
    syncItems();
    fc.setActiveObject(tb);
    fc.requestRenderAll(); rerender();
  };

  const onAddText = () => {
    const fc = fcRef.current; if (!fc) return;
    const { w, h } = sizeRef.current;
    const tb = addTextboxAt(fc, '双击编辑文字', w, h, 0.5, 0.5, null);
    fc.setActiveObject(tb);
    fc.requestRenderAll(); rerender();
  };

  const setProp = (key, val) => {
    const o = activeRef.current; if (!o) return;
    o.set(key, val);
    if (fcRef.current) fcRef.current.requestRenderAll();
    rerender();
  };

  const applyFont = (value) => {
    const o = activeRef.current;
    if (o && o.type && o.type.indexOf('text') === 0) setProp('fontFamily', value);
    else defaultFontRef.current = value;
    rerender();
  };

  // ===== 服务器字体（FontFace 按需加载）=====
  const loadServerFonts = useCallback(async () => {
    try {
      const r = await apiFetch('/api/fonts').then((x) => x.json());
      if (r && r.success) setServerFonts(Array.isArray(r.data) ? r.data : []);
    } catch (e) { /* 字体列表拉取失败静默，不影响画布 */ }
  }, []);

  useEffect(() => { loadServerFonts(); }, [loadServerFonts]);

  const loadFontFace = async (family, url) => {
    try {
      const f = new FontFace(family, 'url(' + url + ')');
      await f.load();
      document.fonts.add(f);
      return true;
    } catch (e) { return false; }
  };

  const applyServerFont = async (f) => {
    setFontLoadingFamily(f.family);
    const ok = await loadFontFace(f.family, f.url);
    setFontLoadingFamily('');
    if (!ok) { message.error('字体加载失败：' + f.family); return; }
    applyFont(f.family);
    message.success('已应用字体 ' + f.family);
  };

  const onUploadFont = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['ttf', 'otf', 'woff', 'woff2'].includes(ext)) { message.warning('仅支持 ttf / otf / woff / woff2 字体文件'); return; }
    if (file.size > 30 * 1024 * 1024) { message.warning('字体文件超过 30MB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setFontUploading(true);
        const res = await apiFetch('/api/fonts/upload', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileBase64: String(reader.result), filename: file.name }),
        }).then((r) => r.json());
        if (res && res.success) { message.success('字体已上传：' + res.family); await loadServerFonts(); }
        else message.error((res && res.error) || '字体上传失败');
      } catch (err) { message.error('字体上传失败'); }
      setFontUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // 一键套用热门文字样式（描边/背景框组合）
  const applyTextStyle = (p) => {
    const o = activeRef.current; if (!o) { message.info('请先选中一段文字'); return; }
    o.set({
      fill: p.fill,
      stroke: p.stroke,
      strokeWidth: p.strokeWidth,
      textBackgroundColor: p.textBackgroundColor || '',
    });
    if (fcRef.current) fcRef.current.requestRenderAll();
    rerender();
  };

  const onDelete = () => {
    const fc = fcRef.current, o = activeRef.current; if (!fc || !o) return;
    const idx = itemsRef.current.findIndex((x) => x.textbox === o);
    if (idx >= 0) itemsRef.current.splice(idx, 1);
    fc.remove(o); fc.discardActiveObject(); activeRef.current = null; setActiveId(null);
    fc.requestRenderAll(); syncItems(); rerender();
  };
  const onLayer = (dir) => {
    const o = activeRef.current; if (!o || !fcRef.current) return;
    if (dir === 'up') o.bringForward(); else o.sendBackwards();
    fcRef.current.requestRenderAll();
  };

  // ===== 裁切 =====
  // 更新选框外四块遮罩（上/下/左/右），视觉上突出保留区
  const updateCropShades = () => {
    const fc = fcRef.current, rect = cropRectRef.current;
    if (!fc || !rect || !cropShadesRef.current) return;
    const { w, h } = sizeRef.current;
    const l = Math.max(0, Math.min(w, rect.left));
    const t = Math.max(0, Math.min(h, rect.top));
    const r = Math.max(0, Math.min(w, rect.left + rect.width * rect.scaleX));
    const b = Math.max(0, Math.min(h, rect.top + rect.height * rect.scaleY));
    const [st, sb, sl, sr] = cropShadesRef.current;
    st.set({ left: 0, top: 0, width: w, height: Math.max(0, t) });
    sb.set({ left: 0, top: b, width: w, height: Math.max(0, h - b) });
    sl.set({ left: 0, top: t, width: Math.max(0, l), height: Math.max(0, b - t) });
    sr.set({ left: r, top: t, width: Math.max(0, w - r), height: Math.max(0, b - t) });
    fc.requestRenderAll();
  };

  // 清除裁切相关对象（选框 + 遮罩）
  const clearCropObjects = () => {
    const fc = fcRef.current; if (!fc) return;
    const objs = [cropRectRef.current].concat(cropShadesRef.current || []).filter(Boolean);
    objs.forEach((o) => { try { fc.remove(o); } catch (e) { /* 忽略 */ } });
    cropRectRef.current = null;
    cropShadesRef.current = null;
  };

  // 进入裁切模式：默认选框 = 画布中央 85% 区域
  const enterCropMode = () => {
    const fc = fcRef.current; if (!fc) return;
    fc.discardActiveObject(); activeRef.current = null; setActiveId(null);
    const { w, h } = sizeRef.current;
    const z = scaleRef.current || 1;
    const cw = w * 0.85, ch = h * 0.85;
    const rect = new fabric.Rect({
      left: (w - cw) / 2, top: (h - ch) / 2, width: cw, height: ch,
      fill: 'rgba(0,0,0,0.02)', stroke: '#D85A30',
      strokeWidth: Math.max(1.5, 2 / z), strokeUniform: true, strokeDashArray: [8 / z, 5 / z],
      hasRotatingPoint: false, lockRotation: true,
      cornerColor: '#D85A30', cornerStrokeColor: '#FFFFFF', cornerSize: 10, transparentCorners: false,
      borderColor: '#D85A30', excludeFromExport: true,
    });
    const mk = () => new fabric.Rect({
      left: 0, top: 0, width: w, height: h, fill: 'rgba(26,16,10,0.45)',
      selectable: false, evented: false, excludeFromExport: true,
    });
    cropShadesRef.current = [mk(), mk(), mk(), mk()];
    cropShadesRef.current.forEach((s) => fc.add(s));
    fc.add(rect);
    fc.bringToFront(rect);
    rect.setCoords();
    cropRectRef.current = rect;
    updateCropShades();
    fc.setActiveObject(rect);
    setCropMode(true);
    fc.requestRenderAll(); rerender();
  };

  // 退出裁切模式（不应用）
  const cancelCrop = () => {
    const fc = fcRef.current; if (!fc) return;
    clearCropObjects();
    fc.discardActiveObject(); activeRef.current = null; setActiveId(null);
    setCropMode(false);
    fc.requestRenderAll(); rerender();
  };

  // 套用比例：以画布内最大可用区域按比例居中放置选框
  const applyCropRatio = (ratio) => {
    setCropRatio(ratio);
    cropRatioRef.current = ratio;
    const fc = fcRef.current, rect = cropRectRef.current;
    if (!fc || !rect || ratio == null) { rerender(); return; }
    const { w, h } = sizeRef.current;
    let cw = w * 0.9, ch = cw / ratio;
    if (ch > h * 0.9) { ch = h * 0.9; cw = ch * ratio; }
    rect.set({ width: cw, height: ch, scaleX: 1, scaleY: 1, left: (w - cw) / 2, top: (h - ch) / 2 });
    rect.setCoords();
    updateCropShades();
    fc.requestRenderAll(); rerender();
  };

  // 应用裁切：底图按选框真实裁剪，画布尺寸同步收窄
  const applyCrop = () => {
    const fc = fcRef.current, rect = cropRectRef.current; if (!fc || !rect) return;
    const { w, h } = sizeRef.current;
    const cw = Math.max(20, Math.round(rect.width * rect.scaleX));
    const ch = Math.max(20, Math.round(rect.height * rect.scaleY));
    const cx = Math.round(Math.max(0, Math.min(w - cw, rect.left)));
    const cy = Math.round(Math.max(0, Math.min(h - ch, rect.top)));
    clearCropObjects();
    fc.discardActiveObject(); activeRef.current = null; setActiveId(null);
    setCropMode(false);
    const img = fc.backgroundImage;
    if (img) img.set({ cropX: cx, cropY: cy, cropWidth: cw, cropHeight: ch, width: cw, height: ch });
    sizeRef.current = { w: cw, h: ch };
    // 画布外的文字图层收回可视范围，避免保存后被裁丢
    itemsRef.current.forEach(({ textbox }) => {
      if (textbox.left > cw || textbox.top > ch || textbox.left < 0 || textbox.top < 0) {
        textbox.set({ left: Math.max(20, Math.min(cw - 20, textbox.left)), top: Math.max(20, Math.min(ch - 20, textbox.top)) });
        textbox.setCoords();
      }
    });
    fitToScreen();
    message.success('已裁切：' + cw + ' × ' + ch + ' px');
    fc.requestRenderAll(); rerender();
  };

  const onSave = async () => {
    const fc = fcRef.current; if (!fc) return;
    setSaving(true);
    try {
      fc.discardActiveObject();
      fc.requestRenderAll();
      await new Promise((r) => setTimeout(r, 60));
      const scale = scaleRef.current || 1;
      const dataURL = fc.toDataURL({ format: 'jpeg', quality: 0.95, multiplier: 1 / scale });
      const res = await apiFetch('/api/upload/image', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileBase64: dataURL }),
      }).then((r) => r.json());
      if (res && res.url) { message.success('已保存'); if (onSaved) onSaved(res.url); if (onClose) onClose(); }
      else message.error((res && res.error) || '保存失败');
    } catch (e) {
      message.error('保存失败：' + (e.message || e));
    }
    setSaving(false);
  };

  const panelStyle = { width: 232, flex: '0 0 232px', background: '#fff', borderRight: '1px solid #F0E2DB', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
  const stageBg = '#ffffff';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', background: '#FBF7F4', color: '#33251F' }}>
      {/* 顶栏 */}
      <div style={{ height: 54, flex: '0 0 54px', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', background: '#fff', borderBottom: '1px solid #F0E2DB' }}>
        <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#6B5D55', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <CloseOutlined /> 返回
        </button>
        <div style={{ fontSize: 15, fontWeight: 600 }}>文字编辑 · 画布</div>
        <div style={{ flex: 1 }} />
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>保存</Button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* 左栏 */}
        <div style={panelStyle}>
          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            {/* 裁切图片 */}
            <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, marginBottom: 8 }}>裁切图片</div>
            {cropMode ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
                  {CROP_RATIOS.map((r) => (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => applyCropRatio(r.value)}
                      style={{
                        padding: '6px 0', cursor: 'pointer', fontSize: 12, borderRadius: 6,
                        border: '1px solid ' + ((cropRatio == null ? r.value == null : Math.abs(cropRatio - r.value) < 0.001) ? '#D85A30' : '#F0E2DB'),
                        background: (cropRatio == null ? r.value == null : Math.abs(cropRatio - r.value) < 0.001) ? '#FBE9E2' : '#fff',
                        color: (cropRatio == null ? r.value == null : Math.abs(cropRatio - r.value) < 0.001) ? '#C14F28' : '#6B5D55',
                      }}
                    >{r.label}</button>
                  ))}
                </div>
                <Button type="primary" block icon={<CheckOutlined />} onClick={applyCrop} style={{ marginBottom: 6 }}>应用裁切</Button>
                <Button block onClick={cancelCrop}>取消裁切</Button>
                <div style={{ fontSize: 11, color: '#B9A79E', lineHeight: 1.6, margin: '8px 2px 14px' }}>
                  拖动红色选框调整位置，拖动四角调整大小；选比例可锁定宽高比。
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={enterCropMode}
                style={{
                  width: '100%', marginBottom: 14, padding: '10px 12px', cursor: 'pointer',
                  border: '1px dashed #D8C4B6', borderRadius: 8, background: 'transparent', color: '#6B5D55', fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <ScissorOutlined /> 进入裁切
              </button>
            )}

            {/* 添加文字 */}
            <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, marginBottom: 8 }}>添加文字</div>
            {TEXT_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => addPresetText(p)}
                style={{
                  display: 'block', width: '100%', marginBottom: 8, padding: '10px 12px', cursor: 'pointer',
                  border: '1px solid #F0E2DB', borderRadius: 8, background: '#FBF7F4', textAlign: 'left',
                }}
              >
                <div style={{ fontWeight: p.weight === 'bold' ? 700 : 500, color: '#33251F', lineHeight: 1.3 }}
                  className="font-preview" data-font="sans-serif">
                  {p.label}
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={onAddText}
              style={{ display: 'block', width: '100%', marginBottom: 14, padding: '8px 12px', cursor: 'pointer', border: '1px dashed #D8C4B6', borderRadius: 8, background: 'transparent', color: '#8A7A72', fontSize: 13 }}
            >
              <PlusOutlined /> 添加文字
            </button>

            {/* 字体 */}
            <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, margin: '4px 0 8px' }}>字体</div>
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => applyFont(f.value)}
                style={{
                  display: 'block', width: '100%', marginBottom: 6, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                  border: '1px solid ' + (isText && active.fontFamily === f.value ? '#D85A30' : '#F0E2DB'),
                  borderRadius: 7, background: isText && active.fontFamily === f.value ? '#FBE9E2' : '#fff',
                }}
              >
                <div style={{ fontSize: 14, lineHeight: 1.2, color: '#33251F', fontFamily: f.value }}>字体预览 Aa</div>
                <div style={{ fontSize: 11, color: '#B9A79E', fontFamily: f.value }}>{f.label}</div>
              </button>
            ))}

            {/* 我的字体（服务器，上传/按需加载） */}
            <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, margin: '10px 0 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>我的字体（{serverFonts.length}）</span>
              <button
                type="button"
                onClick={() => fontFileRef.current && fontFileRef.current.click()}
                disabled={fontUploading}
                style={{ border: '1px dashed #D8C4B6', background: 'transparent', color: '#8A7A72', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {fontUploading ? <LoadingOutlined /> : <UploadOutlined />} 上传
              </button>
              <input ref={fontFileRef} type="file" accept=".ttf,.otf,.woff,.woff2" style={{ display: 'none' }} onChange={onUploadFont} />
            </div>
            {serverFonts.length === 0 && (
              <div style={{ fontSize: 11, color: '#C9B8AC', padding: '4px 2px 8px', lineHeight: 1.6 }}>还没有自定义字体。点「上传」从本地盘里选 ttf/otf/woff 字体，上传后点字体名即可加载使用。</div>
            )}
            {serverFonts.map((f) => (
              <button
                key={f.filename}
                type="button"
                onClick={() => applyServerFont(f)}
                disabled={fontLoadingFamily === f.family}
                style={{
                  display: 'block', width: '100%', marginBottom: 6, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                  border: '1px solid ' + (isText && active.fontFamily === f.family ? '#D85A30' : '#F0E2DB'),
                  borderRadius: 7, background: isText && active.fontFamily === f.family ? '#FBE9E2' : '#FBF7F4',
                }}
              >
                <div style={{ fontSize: 13, lineHeight: 1.2, color: '#33251F', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {fontLoadingFamily === f.family ? <LoadingOutlined style={{ color: '#D85A30' }} /> : null}
                  <span>{f.family}</span>
                </div>
                <div style={{ fontSize: 10, color: '#B9A79E', marginTop: 2 }}>{f.ext} · 点击加载并应用</div>
              </button>
            ))}
          </div>
        </div>

        {/* 中间画布 */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: stageBg }}>
          {baseHasText && (
            <Alert
              type="warning" showIcon style={{ borderRadius: 0 }}
              message="底图仍含文字（自动去字未完全成功），本次未自动叠加文字图层，可点「添加文字」手动新增。"
            />
          )}
          <div
            ref={stageRef}
            style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative' }}
          >
            {loading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, background: stageBg }}>
                <Spin tip="画布加载中…" />
              </div>
            )}
            <div style={{ boxShadow: '0 6px 24px rgba(0,0,0,.14)', border: '1px solid #F0E2DB', display: 'inline-block', lineHeight: 0, position: 'relative' }}>
              <canvas ref={canvasElRef} />
            </div>
          </div>
          {/* 底部信息条 */}
          <div style={{ height: 42, flex: '0 0 42px', display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px', background: '#FBF7F4', borderTop: '1px solid #F0E2DB', color: '#6B5D55', fontSize: 12 }}>
            {cropMode ? (
              <span style={{ color: '#D85A30', fontWeight: 600 }}>✂ 裁切中 · 拖动选框选取保留区域</span>
            ) : (
              <>
                <span>{sizeRef.current.w} × {sizeRef.current.h} px</span>
                <span style={{ color: '#D8C4B6' }}>|</span>
              </>
            )}
            <span>{zoomPct}%</span>
            <div style={{ flex: 1 }} />
            <Tooltip title="缩小"><IconBtn title="缩小" onClick={() => zoomTo(scaleRef.current / 1.25)}><ZoomOutOutlined /></IconBtn></Tooltip>
            <Tooltip title="放大"><IconBtn title="放大" onClick={() => zoomTo(scaleRef.current * 1.25)}><ZoomInOutlined /></IconBtn></Tooltip>
            <Tooltip title="适合窗口"><IconBtn title="适合窗口" onClick={fitToScreen}><ExpandOutlined /></IconBtn></Tooltip>
            <button type="button" onClick={() => zoomTo(1)} style={{ border: '1px solid #D8C4B6', background: 'transparent', color: '#6B5D55', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>100%</button>
          </div>
        </div>

        {/* 右栏 */}
        <div style={{ ...panelStyle, borderRight: 'none', borderLeft: '1px solid #F0E2DB' }}>
          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            {isText ? (
              <>
                <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, marginBottom: 10 }}>文字属性</div>

                <Row label="字体">
                  <select
                    value={active.fontFamily || 'sans-serif'}
                    onChange={(e) => {
                      const v = e.target.value;
                      const sf = serverFonts.find((x) => x.family === v);
                      if (sf) applyServerFont(sf);
                      else setProp('fontFamily', v);
                    }}
                    style={{ width: '100%', height: 32, borderRadius: 6, border: '1px solid #E5DDD6', padding: '0 8px', fontSize: 13, color: '#33251F', background: '#fff' }}
                  >
                    <optgroup label="系统字体">
                      {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </optgroup>
                    {serverFonts.length > 0 && (
                      <optgroup label="我的字体">
                        {serverFonts.map((f) => <option key={f.filename} value={f.family}>{f.family}</option>)}
                      </optgroup>
                    )}
                  </select>
                </Row>

                <Row label="字号">
                  <NumInput value={Math.round(active.fontSize || 16)} min={8} max={400} step={1}
                    onChange={(v) => setProp('fontSize', Math.max(8, v || 8))} suffix="px" />
                </Row>

                <Row label="样式">
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <IconBtn title="加粗" active={active.fontWeight === 'bold'} onClick={() => setProp('fontWeight', active.fontWeight === 'bold' ? 'normal' : 'bold')}><BoldOutlined /></IconBtn>
                    <IconBtn title="斜体" active={active.fontStyle === 'italic'} onClick={() => setProp('fontStyle', active.fontStyle === 'italic' ? 'normal' : 'italic')}><ItalicOutlined /></IconBtn>
                    <IconBtn title="下划线" active={!!active.underline} onClick={() => setProp('underline', !active.underline)}><UnderlineOutlined /></IconBtn>
                  </span>
                </Row>

                <Row label="文字样式（一键套用）">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TEXT_STYLE_PRESETS.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => applyTextStyle(p)}
                        style={{
                          padding: '4px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                          border: '1px solid #E5DDD6', background: '#FBF7F4', color: '#6B5D55',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: p.fill, border: '1px solid rgba(0,0,0,.15)', display: 'inline-block' }} />
                        {p.label}
                      </button>
                    ))}
                  </div>
                </Row>

                <Row label="对齐">
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <IconBtn title="左对齐" active={active.textAlign === 'left'} onClick={() => setProp('textAlign', 'left')}><AlignLeftOutlined /></IconBtn>
                    <IconBtn title="居中" active={active.textAlign === 'center'} onClick={() => setProp('textAlign', 'center')}><AlignCenterOutlined /></IconBtn>
                    <IconBtn title="右对齐" active={active.textAlign === 'right'} onClick={() => setProp('textAlign', 'right')}><AlignRightOutlined /></IconBtn>
                  </span>
                </Row>

                <Row label="行距">
                  <NumInput value={Number((active.lineHeight || 1).toFixed(1))} min={0.5} max={3} step={0.1}
                    onChange={(v) => setProp('lineHeight', Math.max(0.5, v || 1))} />
                </Row>

                <Row label="字间距">
                  <NumInput value={Math.round(active.charSpacing || 0)} min={-100} max={500} step={10}
                    onChange={(v) => setProp('charSpacing', v || 0)} suffix="‰" />
                </Row>

                <Row label="文字颜色">
                  <ColorRow label="填充" value={toHexColor(active.fill)} onChange={(v) => setProp('fill', v)} />
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 2 }}>
                    {COLOR_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() => setProp('fill', c)}
                        style={{
                          width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', padding: 0,
                          background: c,
                          border: '1px solid ' + (toHexColor(active.fill).toLowerCase() === c.toLowerCase() ? '#D85A30' : '#E5DDD6'),
                          boxShadow: c.toLowerCase() === '#ffffff' ? 'inset 0 0 0 2px #fff' : 'none',
                        }}
                      />
                    ))}
                  </div>
                </Row>

                <Row label="背景框">
                  <ColorRow label="文字底色" value={typeof active.textBackgroundColor === 'string' && active.textBackgroundColor.charAt(0) === '#' ? active.textBackgroundColor : '#ffffff'}
                    onChange={(v) => setProp('textBackgroundColor', v)} onClear={() => setProp('textBackgroundColor', '')} clearLabel="无背景" />
                </Row>

                <Row label="描边">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="color" value={typeof active.stroke === 'string' && active.stroke.charAt(0) === '#' ? active.stroke : '#000000'}
                      onChange={(e) => { setProp('stroke', e.target.value); if (!active.strokeWidth) setProp('strokeWidth', 1); }}
                      style={{ width: 34, height: 28, border: '1px solid #E5DDD6', borderRadius: 6, padding: 2, background: '#fff', cursor: 'pointer' }} />
                    <NumInput value={Math.round(active.strokeWidth || 0)} min={0} max={20} step={1}
                      onChange={(v) => setProp('strokeWidth', Math.max(0, v || 0))} suffix="px" />
                  </div>
                </Row>

                <Row label="不透明度">
                  <NumInput value={Math.round((active.opacity == null ? 1 : active.opacity) * 100)} min={0} max={100} step={5}
                    onChange={(v) => setProp('opacity', Math.max(0, Math.min(100, v || 0)) / 100)} suffix="%" />
                </Row>

                <Row label="图层">
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <IconBtn title="上移一层" onClick={() => onLayer('up')}><ArrowUpOutlined /></IconBtn>
                    <IconBtn title="下移一层" onClick={() => onLayer('down')}><ArrowDownOutlined /></IconBtn>
                  </span>
                </Row>

                <Button danger block icon={<DeleteOutlined />} onClick={onDelete} style={{ marginTop: 4 }}>删除该文字</Button>
              </>
            ) : isImage ? (
              <>
                <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, marginBottom: 10 }}>产品元素</div>
                <Row label="图层">
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <IconBtn title="上移一层" onClick={() => onLayer('up')}><ArrowUpOutlined /></IconBtn>
                    <IconBtn title="下移一层" onClick={() => onLayer('down')}><ArrowDownOutlined /></IconBtn>
                  </span>
                </Row>
                <Button danger block icon={<DeleteOutlined />} onClick={onDelete}>删除该元素</Button>
              </>
            ) : (
              <div style={{ textAlign: 'center', paddingTop: 40, color: '#C9B8AC', fontSize: 13, lineHeight: 1.8 }}>
                <FontSizeOutlined style={{ fontSize: 28 }} />
                <div style={{ marginTop: 10 }}>选中画布中的文字</div>
                <div>即可在右侧编辑属性</div>
              </div>
            )}

            {/* 图层清单（底部） */}
            {items.length > 0 && (
              <div style={{ marginTop: 18, borderTop: '1px solid #F0E2DB', paddingTop: 10 }}>
                <div style={{ fontSize: 12, color: '#8A7A72', fontWeight: 600, marginBottom: 6 }}>文字图层（{items.length}）</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 200, overflow: 'auto' }}>
                  {items.map((it, idx) => (
                    <div
                      key={it.id}
                      onClick={() => selectItem(it.id)}
                      style={{
                        padding: '6px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12, lineHeight: 1.4,
                        border: '1px solid ' + (it.id === activeId ? '#D85A30' : 'transparent'),
                        background: it.id === activeId ? '#FBE9E2' : '#FBF7F4', color: it.id === activeId ? '#C14F28' : '#5C524C',
                      }}
                    >
                      <span style={{ display: 'inline-block', width: 18, color: '#C9B8AC' }}>{idx + 1}.</span>
                      <span>{it.label.length > 12 ? it.label.slice(0, 12) + '…' : it.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
